import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Target, Eye, TrendingUp, Home, DollarSign, Users, Briefcase, BookOpen, Zap, Award } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import MultiCTA from "@/components/sections/MultiCTA";
import TrustStrip from "@/components/TrustStrip";
import TeamGrid from "@/components/team/TeamGrid";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { usePageContent } from "@/hooks/usePageContent";
import heroImage from "@/assets/bg-exterior-1.jpg";
import lifestyleImage from "@/assets/about-lifestyle.jpg";
import realtorImage from "@/assets/about-realtor.jpg";
import aboutCtaImage from "@/assets/about-cta.jpg";
import estherPortrait from "@/assets/esther-portrait.jpg";
import localGlobalImage from "@/assets/about-local-global.jpg";
import serviceBuy from "@/assets/service-buy.jpg";
import serviceSell from "@/assets/service-sell.jpg";
import serviceInvest from "@/assets/service-invest.jpg";
import servicePrep from "@/assets/service-prep.jpg";
import whoWeServeBuyers from "@/assets/about-who-buyers.jpg";
import whoWeServeSellers from "@/assets/about-who-sellers.jpg";
import whoWeServeInvestors from "@/assets/about-who-investors.jpg";

const About = () => {
  const { members: featuredTeamMembers } = useTeamMembers({ featuredOnly: true });
  const { getContent } = usePageContent({ pageKey: "about" });
  useEffect(() => {
    document.title = "Hadassah Homes | About Our Real Estate Approach in Massachusetts";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Learn about Hadassah Homes and our mission to educate, empower, and elevate every client. We combine real estate expertise, interior design, and property preparation to help you buy, sell, and invest with confidence.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://hadassahhomesinc.com/about");
    }
    return () => {
      document.title = "Massachusetts Real Estate & Dominican Republic Investment | Hadassah Homes";
      if (metaDesc) metaDesc.setAttribute("content", "Buy, sell, and invest with confidence. Hadassah Homes offers real estate, interior design, and property preparation services in Massachusetts and the Dominican Republic.");
      if (canonical) canonical.setAttribute("href", "https://hadassahhomesinc.com");
    };
  }, []);

  return (
    <SiteLayout>
      {/* SECTION 1 — HERO (H1) */}
      <PageHero
        eyebrow={getContent("hero", "subtitle", "About Hadassah Homes")}
        title={getContent(
          "hero",
          "title",
          "Real Estate Guidance Built Around Education, Empowerment & Elevation",
        )}
        backgroundImage={heroImage}
      />

      {/* SECTION 2 — COMPANY OVERVIEW */}
      <ContentSection>
        <div className="flex flex-col md:flex-row gap-12 md:gap-20 items-start">
          <div className="w-full md:w-7/12 space-y-6">
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">About Hadassah Homes</span>
            <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
              {getContent("intro", "heading", "Real Estate, Design & Strategic Property Guidance")}
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {getContent(
                "intro",
                "body",
                "Hadassah Homes is a real estate and design firm dedicated to helping individuals and families build long-term wealth through property ownership and investment.",
              )}
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Our mission is to educate, empower, and elevate our clients by providing the knowledge, guidance, and strategic insight needed to make confident real estate decisions. We believe informed decisions create lasting impact, and we are committed to supporting our clients at every stage of their journey.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Whether purchasing a home, preparing a property for market, designing a space, or exploring international opportunities such as the Dominican Republic, Hadassah Homes provides trusted guidance rooted in expertise, purpose, and long-term vision.
            </p>
          </div>
          <div className="w-full md:w-5/12">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={lifestyleImage}
                alt="Hadassah Homes guiding clients through real estate and design decisions"
                className="w-full h-full object-cover"
                loading="lazy"
                width={1280}
                height={960}
              />
            </div>
          </div>
        </div>
      </ContentSection>

      {/* SECTION 3 — MEET THE TEAM CARDS */}
      <ContentSection variant="dark">
        <div className="text-center mb-12">
          <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent mb-3 block">Our People</span>
          <h2 className="font-headline text-3xl md:text-5xl text-secondary-foreground leading-tight">Meet the Team</h2>
        </div>
        <TeamGrid members={featuredTeamMembers} className="max-w-3xl" />
      </ContentSection>

      <TrustStrip />

      {/* SECTION 5 — EXPERIENCE & APPROACH */}
      <ContentSection>
        <div className="max-w-3xl mx-auto text-center mb-14">
          <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">Our Approach</span>
          <h2 className="font-headline text-3xl md:text-5xl text-foreground mt-4 leading-tight">
            Experience & Approach
          </h2>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">
            Esther has worked with buyers, sellers, and investors across Massachusetts, helping clients navigate competitive markets and make smart, strategic decisions.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {[
            { icon: MessageSquare, title: "Clear Communication and Transparency", desc: "Guidance that keeps every step understandable, honest, and grounded in trust." },
            { icon: Target, title: "Strategic Pricing and Positioning", desc: "Thoughtful market strategy designed to help each property compete and perform." },
            { icon: Eye, title: "Attention to Detail in Property Presentation", desc: "A careful eye on how properties are prepared, shown, and perceived." },
            { icon: TrendingUp, title: "A Commitment to Long-Term Client Success", desc: "Support built around relationships, confidence, and outcomes that last beyond closing." },
          ].map((item) => (
            <div key={item.title} className="flex gap-5 p-6 bg-card border border-border">
              <item.icon className="w-6 h-6 text-accent flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-headline text-lg text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-10 max-w-3xl mx-auto text-center text-muted-foreground text-base md:text-lg leading-relaxed">
          Rather than taking a transactional approach, Esther focuses on building relationships and ensuring each client feels confident at every stage of the process.
        </p>
      </ContentSection>

      {/* SECTION 6 — STRATEGIC ADVANTAGE */}
      <ContentSection variant="dark">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">What Sets Us Apart</span>
          <h2 className="font-headline text-3xl md:text-5xl text-secondary-foreground mt-4 leading-tight">
            More Than Real Estate — A Strategic Advantage
          </h2>
          <p className="mt-6 text-secondary-foreground/70 text-base md:text-lg leading-relaxed">
            What truly sets Hadassah Homes apart is our full-service approach.
          </p>
          <p className="mt-4 text-secondary-foreground/70 text-base md:text-lg leading-relaxed">
            We don&apos;t just help you buy or sell. We help you position your property to perform at its highest level through thoughtful strategy, interior design real estate insight, and <Link to="/property-preparation" className="text-accent hover:text-accent/80 transition-colors">property preparation services</Link>.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto">
          {[
            {
              icon: Briefcase,
              title: "Real Estate Expertise",
              desc: "Market knowledge and strategy that help clients make strong, informed decisions.",
            },
            {
              icon: Award,
              title: "Interior Design Insight",
              desc: "Design perspective that helps shape spaces buyers respond to and remember.",
            },
            {
              icon: Home,
              title: "Property Preparation Services",
              desc: "Hands-on support that helps every property show at its absolute best.",
            },
          ].map((item) => (
            <div key={item.title} className="text-center">
              <item.icon className="w-8 h-8 text-accent mx-auto mb-5" strokeWidth={1.5} />
              <h3 className="font-headline text-xl text-secondary-foreground mb-3">{item.title}</h3>
              <p className="text-secondary-foreground/65 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-14 text-center text-secondary-foreground/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          From professional cleaning and repairs to strategic design and finishing touches, we help transform properties into spaces that attract attention, increase value, and drive stronger results.
        </p>
      </ContentSection>

      {/* SECTION 7 — REALTORS® STATEMENT */}
      <ContentSection>
        <div className="flex flex-col md:flex-row gap-12 md:gap-20 items-start">
          <div className="w-full md:w-7/12 space-y-6">
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">Our Standard</span>
            {(() => {
              const STANDARD_DEFAULT = "__USE_FALLBACK__";
              const standardHeading = getContent("standard", "heading", STANDARD_DEFAULT);
              return (
                <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
                  {standardHeading === STANDARD_DEFAULT ? (
                    <>More Than Agents —<br className="hidden md:block" /> We Are REALTORS®</>
                  ) : (
                    standardHeading
                  )}
                </h2>
              );
            })()}
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {getContent(
                "standard",
                "body",
                "At Hadassah Homes, we are more than just agents — we are REALTORS®, bound by a higher standard of ethics, integrity, and commitment to our clients and communities.",
              )}
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Being a REALTOR® means holding ourselves accountable to more than just transactions. It means advocating for fairness, honesty, and professionalism in every interaction. It means ensuring that every client — regardless of background or circumstance — is treated with respect and given the knowledge and resources to make empowered decisions.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Our team stands for education, empowerment, and elevation. We believe real estate is more than buying or selling property — it's about creating pathways to opportunity, building generational wealth, and strengthening the communities we serve.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Every REALTOR® on our team embraces the Realtor's Code of Ethics and embodies the Hadassah Homes mission:
            </p>
            <blockquote className="pl-6 border-l-2 border-accent">
              <p className="font-headline text-xl md:text-2xl text-foreground italic leading-snug">
                "To educate, empower, and elevate every individual we encounter — because every moment is a teaching and learning moment."
              </p>
            </blockquote>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              When you work with a Hadassah Homes REALTOR®, you're not just getting representation — you're gaining a trusted partner who cares about your growth, your goals, and your future.
            </p>
          </div>
          <div className="w-full md:w-5/12">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={realtorImage}
                alt="REALTOR guidance and homeownership support"
                className="w-full h-full object-cover"
                loading="lazy"
                width={1280}
                height={960}
              />
            </div>
          </div>
        </div>
      </ContentSection>

      {/* SECTION 8 — WHO WE SERVE */}
      <MultiCTA
        headline="Who We Serve"
        description="We support clients at every stage, from buying and selling to preparing and improving properties."
        paths={[
          {
            icon: Home,
            title: "Home Buyers",
            description: "For buyers who want clarity, guidance, and a competitive edge throughout the process.",
            image: whoWeServeBuyers,
            cta: { text: "Explore Buying", href: "/buy-a-home" },
          },
          {
            icon: DollarSign,
            title: "Home Sellers",
            description: "For sellers who want to maximize value and stand out in today&apos;s market.",
            image: whoWeServeSellers,
            cta: { text: "Explore Selling", href: "/sell-your-home" },
          },
          {
            icon: TrendingUp,
            title: "Real Estate Investors",
            description: "For investors focused on building long-term wealth through smart decisions.",
            image: whoWeServeInvestors,
            cta: { text: "Explore Investing", href: "/invest" },
          },
        ]}
      />
      <ContentSection className="pt-6 md:pt-8">
        <div className="mx-auto max-w-wide border-t border-border/70 pt-16 md:pt-20">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <h3 className="font-headline text-2xl md:text-3xl text-foreground">
            Property Preparation Services
          </h3>
          <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
            Get your property market-ready, move-in ready, or tenant-ready with expert support.
          </p>
        </div>
        <MultiCTA
          compact
          columns={2}
          paths={[
            {
              icon: Home,
              title: "Pre-Sale Preparation",
              description: "Cleaning, repairs, and finishing touches that help your home show better and sell stronger.",
              image: serviceSell,
              cta: { text: "Prepare to Sell", href: "/sell-your-home" },
            },
            {
              icon: Home,
              title: "Home Improvements",
              description: "Carpentry, painting, and upgrades designed to increase value and appeal.",
              image: servicePrep,
              cta: { text: "View Services", href: "/property-preparation" },
            },
            {
              icon: Users,
              title: "Ongoing Home Care",
              description: "Reliable cleaning and upkeep for busy homeowners who want a consistently well-maintained space.",
              image: lifestyleImage,
              cta: { text: "Explore Cleaning", href: "/cleaning" },
            },
            {
              icon: TrendingUp,
              title: "Turnover Services",
              description: "Fast, efficient preparation for rentals, Airbnb properties, and tenant transitions.",
              image: serviceInvest,
              cta: { text: "Explore Prep Services", href: "/property-preparation" },
            },
          ]}
        />
        </div>
      </ContentSection>
      <ContentSection>
        <p className="max-w-3xl mx-auto text-center text-muted-foreground text-base md:text-lg leading-relaxed">
          No matter where you are in your journey, our goal is to provide the insight and support you need to succeed.
        </p>
      </ContentSection>

      {/* SECTION 9 — LOCAL + GLOBAL */}
      <ContentSection variant="dark">
        <div className="flex flex-col md:flex-row gap-12 md:gap-20 items-center">
          <div className="w-full md:w-7/12 space-y-5">
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">Our Reach</span>
          <h2 className="font-headline text-3xl md:text-5xl text-secondary-foreground leading-tight">
            Local Expertise. Global Opportunities.
          </h2>
          <p className="text-secondary-foreground/80 text-base md:text-lg leading-relaxed">
            We proudly serve clients throughout Massachusetts while also guiding buyers and investors in the Dominican Republic, bringing local knowledge to both real estate Massachusetts opportunities and Dominican Republic real estate decisions.
          </p>
          <p className="text-secondary-foreground/80 text-base md:text-lg leading-relaxed">
            Whether you&apos;re looking for a primary residence, a second home, or an investment property, we provide the knowledge and support needed to navigate each market with clarity and confidence.
          </p>
          <p className="text-secondary-foreground/80 text-base md:text-lg leading-relaxed">
            Our approach is always rooted in transparency, strategy, and long-term value.
          </p>
          <p className="text-secondary-foreground/80 text-base md:text-lg leading-relaxed">
            When you&apos;re ready to move forward, you can <Link to="/consultation" className="text-accent hover:text-accent/80 transition-colors">book a consultation</Link> and take the next step with expert guidance.
          </p>
          </div>
          <div className="w-full md:w-5/12">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={localGlobalImage}
                alt="Massachusetts and Dominican Republic real estate markets"
                className="w-full h-full object-cover"
                loading="lazy"
                width={1280}
                height={960}
              />
            </div>
          </div>
        </div>
      </ContentSection>

      {/* SECTION 10 — CORE PRINCIPLES */}
      <ContentSection>
        <div className="max-w-3xl mx-auto text-center mb-14">
          <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">Our Principles</span>
          <h2 className="font-headline text-3xl md:text-5xl text-foreground mt-4 leading-tight">
            Our Commitment to Every Client
          </h2>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">
            Everything we do is built around three core principles:
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
          {[
            { icon: BookOpen, title: "Educate", desc: "We break down every step so you fully understand your options and the process ahead." },
            { icon: Zap, title: "Empower", desc: "We give you the strategy and confidence to move forward without hesitation." },
            { icon: Award, title: "Elevate", desc: "We position your property and your investment for the strongest possible outcome." },
          ].map((item) => (
            <div key={item.title} className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-accent/10 flex items-center justify-center">
                <item.icon className="w-7 h-7 text-accent" strokeWidth={1.5} />
              </div>
              <h3 className="font-headline text-2xl text-foreground mb-3">{item.title}</h3>
              <p className="text-muted-foreground text-base leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* SECTION 11 — FINAL CTA */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={aboutCtaImage}
          alt="Hadassah Homes consultation call to action"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-black/30" />
        <div className="relative z-20 mx-auto max-w-wide px-5 md:px-12">
          <div className="mx-auto max-w-2xl text-center space-y-6 md:space-y-7">
            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent">
              Next Step
            </p>
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-white">
              {getContent("final_cta", "heading", "Let's Build Your Next Move")}
            </h2>
            <p className="text-white/90 text-base md:text-lg leading-[1.8]">
              {getContent(
                "final_cta",
                "body",
                "Real estate is more than a transaction. It's a step toward your future. At Hadassah Homes, we're here to guide you with clarity, strategy, and care.",
              )}
            </p>
            <Link
              to={getContent("final_cta", "button_url", "/consultation")}
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] px-10 py-4 bg-accent text-white hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black/70"
            >
              {getContent("final_cta", "button_label", "Book a Consultation")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default About;
